import { Component, ReactElement } from "react";

import { ApiJob, ApiJobContent } from "@common/api/v1.1/types/jobs";
import { DocumentSection } from "../../../splitting/components/Splitting/StateManager/state";
import { Color } from "../../../splitting/components/Splitting/helpers";
import { TaxonomyApi } from "../../api/v1.1/actions/TaxonomyApi";

export type ResolvedContent = Readonly<ApiJobContent & { contents: any }>;

type ChildrenArgs = Readonly<{
  contents: ReadonlyArray<ResolvedContent>;
  failed: boolean;
  progress: Readonly<{
    current: number;
    total: number;
  }>;
  ready: boolean;
}>;

type Props = Readonly<{
  allowFailures?: ReadonlyArray<string>;
  auth: string;
  children: (args: ChildrenArgs) => ReactElement;
  content: ReadonlyArray<ApiJobContent>;
  maxFetchAttempts?: number;
  job: ApiJob;
}>;

type State = Readonly<{
  failed: boolean;
  fetchedContent: ChildrenArgs["contents"];
}>;

export class ContentLoader extends Component<Props, State> {
  public static MAX_RETRIES = 5;
  public static RESIZE_HEIGHT = 1024;
  constructor(props: Props) {
    super(props);
    this.state = {
      failed: false,
      fetchedContent: props.content.map(c => ({
        ...c,
        contents: undefined,
      })),
    };
  }

  public componentDidMount() {
    this.fetchContent();
  }

  public componentWillUnmount() {
    const { fetchedContent } = this.state;
    for (const content of fetchedContent) {
      switch (content.type) {
        case "image/png": {
          if (content.contents === undefined) {
            continue;
          }
          try {
            URL.revokeObjectURL(content.contents);
          } catch (err) {
            continue;
          }
          break;
        }
      }
    }
  }

  public render() {
    const { children } = this.props;
    const { failed, fetchedContent } = this.state;
    const countFetched = fetchedContent.filter(c => c.contents !== undefined).length;
    return children({
      contents: fetchedContent,
      failed,
      progress: { current: countFetched, total: fetchedContent.length },
      ready: countFetched >= fetchedContent.length,
    });
  }

  private async fetchContent(previousCount: number = 0) {
    const { fetchedContent } = this.state;
    if (fetchedContent.length < 1) {
      return;
    }
    let successCount = 0;

    await Promise.all(
      fetchedContent.map(async (c, i) => {
        if (c.contents !== undefined) {
          successCount++;
          return;
        }
        try {
          const fetchedResult = await this.fetchSingleContent(i);
          this.setState(s => ({
            ...s,
            fetchedContent: [
              ...s.fetchedContent.slice(0, i),
              {
                ...s.fetchedContent[i],
                contents: fetchedResult,
              },
              ...s.fetchedContent.slice(i + 1),
            ],
          }));
          successCount++;
        } catch (err) {
          console.error(err);
          return;
        }
      }),
    );
    if (successCount >= fetchedContent.length) {
      return;
    }
    if (previousCount < successCount) {
      // retry as long as we're making progress
      setTimeout(() => {
        this.fetchContent(successCount);
      }, 0);
      return;
    }
    this.setState(s => ({
      ...s,
      failed: true,
    }));
  }

  private async fetchSingleContent(index: number): Promise<any> {
    const { auth, allowFailures, job } = this.props;
    const content = this.state.fetchedContent[index];
    // Try to get it from the new api, if it hasn't been moved yet,
    // fall back to the job's local copy
    if (content.role === 'mapping-taxonomy-json') {
      let taxiApi = new TaxonomyApi(auth);
      try {
        let taxi = await taxiApi.get(job.metadata.mapping.taxonomy)
        return taxi;
      } catch (e) {
        console.log("Taxi not in DB, using legacy GCS access instead")
      }
    }   
    const result = await fetch(content.uri, {
      cache: "no-store",
      headers: {
        Authorization: `Basic ${auth}`,
      },
    });
    if (result.ok) {
      switch (content.type) {
        case "application/json": {
          try {
            const decoded = await result.json();
            return decoded;
          } catch (err) {
            console.error(err);
            if (Array.isArray(allowFailures) && allowFailures.includes(content.role)) {
              return null;
            }
            throw new Error(`Job content for: ${content.role}/${content.order} is invalid JSON.`);
          }
        }
        case "image/png": {
          const buff = await result.blob();
          return this.resizeImage(buff);
        }
        case "application/xml":
        default:
          return result.text();
      }
    }
    if (Array.isArray(allowFailures) && allowFailures.includes(content.role)) {
      return null;
    }
    throw new Error(`Unable to retrieve job content for: ${content.role}/${content.order}`);
  }
  private resizeImage(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(blob);
      const image = new Image();
      image.onerror = reject;
      image.onload = () => {
        if (image.naturalHeight <= ContentLoader.RESIZE_HEIGHT) {
          // no need to resize images this small
          resolve(objectUrl);
          return;
        }
        const scale = ContentLoader.RESIZE_HEIGHT / image.naturalHeight;
        const newWidth = Math.floor(scale * image.naturalWidth);
        const newHeight = ContentLoader.RESIZE_HEIGHT;
        const canvas = document.createElement("canvas");
        if (!canvas) {
          reject(new Error("Unable to create canvas for image resize."));
          return;
        }
        canvas.width = newWidth;
        canvas.height = newHeight;
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("Unable to create canvas context for image resize."));
          return;
        }
        context.drawImage(image, 0, 0, newWidth, newHeight);
        canvas.toBlob((b: Blob | null) => {
          if (b) {
            URL.revokeObjectURL(objectUrl);
            resolve(URL.createObjectURL(b));
        }
        });
      };
      image.src = objectUrl;
    });
  }
}
